// Verification flow — the multi-step part of registration.
//
// On `POST /auth/register` the server creates a pending account and fires a
// verification challenge. The requested email/phone is claimed onto `users`
// only after the user proves ownership:
//   - email registrations: emails a one-time link `<APP_URL>/verify-email?token=...`
//   - phone registrations: SMS-es a 6-digit code
//
// The user can then call:
//   - `POST /auth/verify/email`   { token }            → marks email verified
//   - `POST /auth/verify/phone`   { phone, code }      → marks phone verified
//   - `POST /auth/verify/resend`  { kind, identifier } → re-issues the challenge
//
// The verification status is a hard boundary for self-registered accounts:
// a usable app session starts only after the user proves email or phone
// ownership. Admin-provisioned username-only accounts are the exception.
import { z } from 'zod';
import { Email, Locale, Phone } from './requests.js';
import { RoleId } from '../members/role.js';
/** Challenge metadata returned alongside register and authenticated resend so
 *  the frontend can render the correct step-2 view (link vs code, plus
 *  countdown). Public resend probes return `challenge: null` to avoid account
 *  enumeration. */
export const VerificationChallenge = z.object({
    kind: z.enum(['email', 'phone']),
    /** The address the challenge was sent to — masked on the wire so it's safe
     *  to surface verbatim in the UI ("we sent a link to a***@example.com"). */
    sentTo: z.string(),
    /** Time the challenge stops being valid (UTC ISO-8601). */
    expiresAt: z.string().datetime({ offset: true }),
    /** Earliest moment the user is allowed to request a fresh challenge.
     *  The server enforces this; the frontend uses it to render a countdown. */
    resendAvailableAt: z.string().datetime({ offset: true }),
});
/** Email verification — body posted to `/auth/verify/email`.
 *  Accepts ONLY the raw token; the user is identified by the token row. */
export const EmailVerifyRequest = z.object({
    token: z.string().min(1).max(256),
});
/** Phone verification — body posted to `/auth/verify/phone`.
 *  We require the phone alongside the code so anonymous spray attacks can't
 *  brute-force globally; the (phone, code) tuple is the lookup key. */
export const PhoneVerifyRequest = z.object({
    phone: Phone,
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/u, 'Verification code must be 6 digits'),
});
/** Resend the challenge. The session cookie identifies the user — no need
 *  to repeat the identifier on the wire — but for unauthenticated callers
 *  who are mid-flow we accept either an email OR a phone, mirroring the
 *  shape used at register time. */
export const ResendVerificationRequest = z
    .object({
    kind: z.enum(['email', 'phone']),
    email: Email.optional(),
    phone: Phone.optional(),
})
    .superRefine((val, ctx) => {
    if (val.kind === 'email' && val.email === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'email is required when kind=email',
        });
    }
    if (val.kind === 'phone' && val.phone === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['phone'],
            message: 'phone is required when kind=phone',
        });
    }
});
export const VerificationResponse = z.object({
    ok: z.literal(true),
    /** Always set after a successful verify; mirrors the field on AuthUser. */
    emailVerifiedAt: z.string().datetime({ offset: true }).nullable(),
    phoneVerifiedAt: z.string().datetime({ offset: true }).nullable(),
    /** Present only when this verification created or refreshed a real app
     * session. Re-clicking an already-consumed link remains idempotent but does
     * not grant a new session. */
    session: z
        .object({
        user: z.object({
            id: z.string().uuid(),
            email: z.string().email().nullable(),
            phone: z.string().nullable(),
            username: z.string().nullable(),
            displayName: z.string(),
            role: RoleId,
            locale: Locale,
            createdAt: z.string().datetime({ offset: true }),
            emailVerifiedAt: z.string().datetime({ offset: true }).nullable(),
            phoneVerifiedAt: z.string().datetime({ offset: true }).nullable(),
        }),
        requiresVerification: z.boolean(),
    })
        .nullable(),
});
export const ResendVerificationResponse = z.object({
    ok: z.literal(true),
    challenge: VerificationChallenge.nullable(),
});
/** Mask the middle of an identifier for safe display.
 *  - email:  `a***e@example.com`  (keeps first + last char of local part)
 *  - phone:  `+12025*****34`     (keeps prefix + last 2 digits)
 *  - other:  unchanged */
export function maskIdentifier(kind, value) {
    if (kind === 'email') {
        const at = value.indexOf('@');
        if (at <= 1)
            return value;
        const local = value.slice(0, at);
        const domain = value.slice(at);
        if (local.length <= 2)
            return `${local[0] ?? ''}*${domain}`;
        return `${local[0] ?? ''}${'*'.repeat(local.length - 2)}${local[local.length - 1] ?? ''}${domain}`;
    }
    // phone
    if (value.length <= 4)
        return value;
    const head = value.startsWith('+') ? value.slice(0, 3) : value.slice(0, 2);
    const tail = value.slice(-2);
    return `${head}${'*'.repeat(Math.max(2, value.length - head.length - tail.length))}${tail}`;
}
//# sourceMappingURL=verification.js.map